/**
 * Клип на экране кадров: цвет статуса, чип-буква хронологии, плашка карточки кадра. Подпись модели —
 * из справочника `/api/video/options` (нет справочника — код модели).
 */

import clsx from "clsx";
import { useMemo } from "react";
import { Link } from "react-router";

import { clipLine, useVideoOptions, type ClipStatus, type TimelineClip } from "@/lib/video";

/** Плашка: фон и текст по статусу. */
export const CLIP_PILL: Record<ClipStatus, string> = {
  queued: "bg-warn-bg text-warn",
  running: "bg-accent-dim text-accent-text",
  done: "bg-ok-bg text-ok",
  failed: "bg-err-bg text-err",
  cancelled: "bg-neutral-bg text-fg-faint",
};

/** `code → label` моделей видео. */
export function useModelLabels(): (code: string) => string {
  const options = useVideoOptions();
  const labels = useMemo(
    () => new Map((options.data?.models ?? []).map((m) => [m.code, m.label])),
    [options.data],
  );
  return (code) => labels.get(code) ?? code;
}

const title = (clip: TimelineClip, label: string) => clipLine(clip, label) + (clip.error ? ` — ${clip.error}` : "");

/** Чип-буква модели на хронологии (K, H, S, S2…) цветом статуса; подсказка — модель · статус · цена. */
export function ClipChip({ clip, label, letter }: { clip: TimelineClip; label: string; letter: string }) {
  return (
    <span
      title={title(clip, label)}
      aria-label={clipLine(clip, label)}
      className={clsx(
        "inline-flex h-[18px] min-w-[18px] flex-none items-center justify-center rounded-[4px] px-1 text-[10.5px] leading-none font-semibold",
        CLIP_PILL[clip.status],
        clip.status === "running" && "animate-pulse",
      )}
    >
      {letter}
    </span>
  );
}

/** Плашка «модель · статус · цена»; готовый клип ведёт в карточку результата. */
export function ClipPill({ clip, label }: { clip: TimelineClip; label: string }) {
  const cls = clsx("inline-flex h-6 items-center rounded-full px-2 text-[11.5px] tnum", CLIP_PILL[clip.status]);
  if (clip.variant_id !== null)
    return (
      <Link to={`/results/${clip.variant_id}`} title={title(clip, label)} className={clsx(cls, "hover:underline")}>
        {clipLine(clip, label)}
      </Link>
    );
  return (
    <span title={title(clip, label)} className={cls}>
      {clipLine(clip, label)}
    </span>
  );
}
